<template>
  <Transition name="cleo-panel-slide">
    <div
      v-if="chatStore.isSidePanelOpen"
      class="fixed right-0 top-0 h-screen flex flex-col overflow-hidden z-[50]"
      :style="{ width: chatStore.sidePanelWidth + 'px', boxShadow: '-12px 0 48px rgba(0,30,80,0.20)', borderLeft: '1px solid rgba(0,74,173,0.10)' }"
    >
      <div
        class="cleo-resize-handle absolute left-0 top-0 h-full w-1.5 z-[60] cursor-col-resize transition-colors"
        style="touch-action: none;"
        title="Drag to resize"
        @pointerdown="startResize"
      />
      <div class="shrink-0 relative overflow-hidden" style="background: linear-gradient(130deg, #002a66 0%, #004aad 55%, #0d5dc8 100%)">
        <div class="absolute inset-0 opacity-[0.07]" style="background-image: linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px); background-size: 22px 22px;" />
        <div class="absolute -top-10 -right-6 w-40 h-40 rounded-full pointer-events-none" style="background: radial-gradient(circle, rgba(96,165,250,0.35), transparent 70%)" />
        <div class="absolute -bottom-12 left-10 w-36 h-36 rounded-full pointer-events-none" style="background: radial-gradient(circle, rgba(255,255,255,0.12), transparent 70%)" />
        <div class="relative flex items-center gap-3 px-4 py-3.5">
          <div class="flex items-center justify-center w-10 h-10 rounded-xl shrink-0 overflow-hidden"
               style="background: rgba(255,255,255,0.14); border: 1px solid rgba(255,255,255,0.25); box-shadow: 0 4px 14px rgba(0,20,60,0.25);">
            <CleoThinkingIcon
              v-if="chatStore.isSending"
              :function-name="getPendingFn(lastAgentMsg?.traceSteps)"
              size-class="w-9 h-9"
            />
            <svg v-else viewBox="0 0 48 52" class="w-9 h-9" fill="none">
              <defs>
                <radialGradient id="sp-ai-head" cx="35%" cy="25%" r="70%">
                  <stop offset="0%" stop-color="#FCD34D"/>
                  <stop offset="60%" stop-color="#F59E0B"/>
                  <stop offset="100%" stop-color="#D97706"/>
                </radialGradient>
              </defs>
              <circle cx="24" cy="4.6" r="3.5" fill="#FCD34D" stroke="#F59E0B" stroke-width="0.5"/>
              <circle cx="23" cy="3.6" r="1.3" fill="white" fill-opacity="0.55"/>
              <rect x="22.5" y="6" width="3" height="7" rx="1.5" fill="#F59E0B"/>
              <circle cx="6" cy="28" r="4.5" fill="#D97706"/>
              <circle cx="42" cy="28" r="4.5" fill="#D97706"/>
              <rect x="8" y="11" width="32" height="33" rx="12" fill="url(#sp-ai-head)"/>
              <ellipse cx="20" cy="17" rx="8" ry="4.5" fill="white" fill-opacity="0.25" transform="rotate(-8 20 17)"/>
              <path d="M22.8 6.9 C 20.4 4, 16.2 3.6, 14.4 5.2 C 12.6 6.8, 12.6 10.4, 14.4 12 C 16.2 13.6, 20.4 13, 22.8 10.3 Z M25.2 6.9 C 27.6 4, 31.8 3.6, 33.6 5.2 C 35.4 6.8, 35.4 10.4, 33.6 12 C 31.8 13.6, 27.6 13, 25.2 10.3 Z" fill="#004AAD" stroke="#93c5fd" stroke-width="0.5" stroke-opacity="0.6"/>
              <circle cx="24" cy="8.6" r="2.2" fill="#003a8a"/>
              <circle cx="17.2" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
              <circle cx="16.4" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
              <circle cx="30.8" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
              <circle cx="31.6" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
              <circle cx="16" cy="26" r="6.5" fill="white"/>
              <circle cx="16" cy="26" r="3.8" fill="#004AAD"/>
              <circle cx="17.1" cy="24.6" r="1.4" fill="white" fill-opacity="0.8"/>
              <circle cx="32" cy="26" r="6.5" fill="white"/>
              <circle cx="32" cy="26" r="3.8" fill="#004AAD"/>
              <circle cx="33.1" cy="24.6" r="1.4" fill="white" fill-opacity="0.8"/>
              <path d="M16 37 Q24 43 32 37" stroke="#92400E" stroke-width="2.2" stroke-linecap="round" fill="none" opacity="0.75"/>
              <ellipse cx="9" cy="30" rx="4.5" ry="3" fill="#F97316" fill-opacity="0.22"/>
              <ellipse cx="39" cy="30" rx="4.5" ry="3" fill="#F97316" fill-opacity="0.22"/>
              <path d="M10.8 23 L 8.8 21.5 M12.4 20.6 L 11.3 18.7" stroke="#92400E" stroke-width="1.6" stroke-linecap="round" opacity="0.85"/>
              <path d="M37.2 23 L 39.2 21.5 M35.6 20.6 L 36.7 18.7" stroke="#92400E" stroke-width="1.6" stroke-linecap="round" opacity="0.85"/>
              <rect x="20.5" y="42.5" width="7" height="3.6" fill="#D97706"/>
              <circle cx="12.6" cy="48.8" r="3.4" fill="#004AAD"/>
              <circle cx="35.4" cy="48.8" r="3.4" fill="#004AAD"/>
              <path d="M13 46.2 Q24 51.8 35 46.2 L 39.5 52 L 8.5 52 Z" fill="#004AAD"/>
              <path d="M18.6 47.9 Q24 50.8 29.4 47.9 L 27.8 50.2 Q24 52 20.2 50.2 Z" fill="white" fill-opacity="0.9"/>
            </svg>
          </div>

          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <p class="text-white text-sm font-bold tracking-wide leading-tight">Cleo</p>
              <span class="flex items-center gap-1 px-1.5 py-0.5 rounded-full" style="background: rgba(52,211,153,0.14); border: 1px solid rgba(52,211,153,0.3);">
                <span class="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span class="text-[10px] text-emerald-300 font-medium">Online</span>
              </span>
            </div>
            <p class="text-white/50 text-[10px] mt-0.5">AI Assistant</p>
          </div>

          <button
            v-if="chatStore.dbConversationId !== null"
            class="cleo-header-btn flex items-center justify-center w-8 h-8 rounded-lg transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
            :title="chatStore.isReported ? 'Problem reported — thank you' : 'Report a problem with this conversation'"
            :disabled="chatStore.isReported"
            @click="handleReportProblem"
          >
            <svg class="w-4 h-4" :class="chatStore.isReported ? 'text-amber-300' : 'text-white/75'" :fill="chatStore.isReported ? 'currentColor' : 'none'" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2z" />
            </svg>
          </button>

          <button
            class="cleo-header-btn flex items-center justify-center w-8 h-8 rounded-lg transition-all active:scale-95"
            :class="historyOpen ? 'cleo-header-btn-active' : ''"
            title="Conversation history"
            @click="openHistory"
          >
            <svg class="w-4 h-4 text-white/75" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>

          <button
            class="cleo-header-btn flex items-center justify-center w-8 h-8 rounded-lg transition-all active:scale-95"
            title="New conversation"
            @click="handleReset"
          >
            <svg class="w-4 h-4 text-white/75" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>

          <button
            class="cleo-header-btn flex items-center justify-center w-8 h-8 rounded-lg transition-all active:scale-95"
            title="Close"
            @click="chatStore.closeSidePanel()"
          >
            <svg class="w-4 h-4 text-white/75" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <div class="flex-1 relative overflow-hidden flex flex-col">
      <div ref="messagesContainer" class="cleo-scroll flex-1 overflow-y-auto py-4 px-4 space-y-3" style="background: linear-gradient(180deg, #f6f8fd 0%, #edf2fa 100%);">
        <div
          v-if="chatStore.messages.length === 0 && !chatStore.isGreeting"
          class="flex flex-col items-center justify-center h-full text-center gap-4 pb-6 px-4"
        >
          <div class="relative">
            <div class="absolute inset-0 rounded-full blur-xl" style="background: rgba(0,74,173,0.14)" />
            <div class="relative w-24 h-24 rounded-full bg-white flex items-center justify-center" style="border: 1px solid rgba(0,74,173,0.12); box-shadow: 0 8px 24px rgba(0,74,173,0.12);">
              <CleoMoodIcon mood="friendly" class="w-14 h-14" />
            </div>
          </div>
          <div>
            <p class="text-base font-bold text-gray-800">Hi! I'm Cleo</p>
            <p class="text-xs text-gray-400 mt-1">Ask me about your shipments, invoices or orders.</p>
          </div>
          <div class="flex flex-wrap justify-center gap-1.5">
            <span class="cleo-topic-pill">Shipments</span>
            <span class="cleo-topic-pill">Invoices</span>
            <span class="cleo-topic-pill">Orders</span>
          </div>
        </div>

        <template v-for="(msg, index) in chatStore.messages" :key="index">
          <div v-if="msg.role === 'user'" class="flex justify-end">
            <div
              class="max-w-[80%] text-white text-sm px-3.5 py-2.5 rounded-2xl rounded-tr-sm leading-relaxed"
              style="background: linear-gradient(135deg, #0a58c4, #003a8a); box-shadow: 0 3px 10px rgba(0,74,173,0.28);"
            >
              {{ msg.content }}
            </div>
          </div>

          <div v-else class="flex justify-start gap-2 items-end">
            <div class="shrink-0 w-8 h-8 flex items-center justify-center">
              <CleoThinkingIcon
                v-if="chatStore.isSending && index === chatStore.messages.length - 1"
                :function-name="getPendingFn(msg.traceSteps)"
                size-class="w-8 h-8"
              />
              <svg v-else viewBox="0 0 48 52" class="w-8 h-8" fill="none">
                <circle cx="24" cy="4.6" r="3" fill="#FCD34D"/>
                <rect x="22.5" y="6" width="3" height="5.5" rx="1.5" fill="#F59E0B"/>
                <circle cx="6" cy="28" r="3.5" fill="#D97706"/>
                <circle cx="42" cy="28" r="3.5" fill="#D97706"/>
                <rect x="8" y="11" width="32" height="33" rx="12" fill="#F59E0B"/>
                <ellipse cx="20" cy="17" rx="7" ry="4" fill="white" fill-opacity="0.2" transform="rotate(-8 20 17)"/>
                <path d="M22.8 6.9 C 20.4 4, 16.2 3.6, 14.4 5.2 C 12.6 6.8, 12.6 10.4, 14.4 12 C 16.2 13.6, 20.4 13, 22.8 10.3 Z M25.2 6.9 C 27.6 4, 31.8 3.6, 33.6 5.2 C 35.4 6.8, 35.4 10.4, 33.6 12 C 31.8 13.6, 27.6 13, 25.2 10.3 Z" fill="#004AAD" stroke="#93c5fd" stroke-width="0.5" stroke-opacity="0.6"/>
                <circle cx="24" cy="8.6" r="2.2" fill="#003a8a"/>
                <circle cx="17.2" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
                <circle cx="16.4" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
                <circle cx="30.8" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
                <circle cx="31.6" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
                <circle cx="16" cy="26" r="6.5" fill="white"/>
                <circle cx="16" cy="26" r="3.6" fill="#004AAD"/>
                <circle cx="17" cy="24.8" r="1.3" fill="white" fill-opacity="0.85"/>
                <circle cx="32" cy="26" r="6.5" fill="white"/>
                <circle cx="32" cy="26" r="3.6" fill="#004AAD"/>
                <circle cx="33" cy="24.8" r="1.3" fill="white" fill-opacity="0.85"/>
                <path d="M16 37 Q24 43 32 37" stroke="#92400E" stroke-width="2" stroke-linecap="round" fill="none" opacity="0.7"/>
                <path d="M10.8 23 L 8.8 21.5 M12.4 20.6 L 11.3 18.7" stroke="#92400E" stroke-width="1.6" stroke-linecap="round" opacity="0.85"/>
                <path d="M37.2 23 L 39.2 21.5 M35.6 20.6 L 36.7 18.7" stroke="#92400E" stroke-width="1.6" stroke-linecap="round" opacity="0.85"/>
                <rect x="20.5" y="42.5" width="7" height="3.6" fill="#D97706"/>
                <circle cx="12.6" cy="48.8" r="3.4" fill="#004AAD"/>
                <circle cx="35.4" cy="48.8" r="3.4" fill="#004AAD"/>
                <path d="M13 46.2 Q24 51.8 35 46.2 L 39.5 52 L 8.5 52 Z" fill="#004AAD"/>
                <path d="M18.6 47.9 Q24 50.8 29.4 47.9 L 27.8 50.2 Q24 52 20.2 50.2 Z" fill="white" fill-opacity="0.9"/>
              </svg>
            </div>
            <div class="max-w-[80%] flex flex-col gap-1">
              <CleoToolPanel
                v-if="msg.traceSteps && msg.traceSteps.length > 0"
                :events="traceStepsToEvents(msg.traceSteps)"
                :is-live="chatStore.isSending && index === chatStore.messages.length - 1"
              />
              <CleoErrorBubble
                v-if="msg.error"
                :error="msg.error"
                :can-retry="index === chatStore.messages.length - 1"
                :busy="chatStore.isSending"
                @retry="chatStore.retryLastUserMessage"
              />
              <div
                v-if="msg.content || (!chatStore.isSending && !msg.error) || (index !== chatStore.messages.length - 1 && !msg.error)"
                class="cleo-markdown text-gray-800 text-sm px-3.5 py-2.5 rounded-2xl rounded-tl-sm leading-relaxed bg-white"
                style="box-shadow: 0 1px 3px rgba(15,23,42,0.06); border: 1px solid rgba(15,23,42,0.08);"
                v-html="renderMarkdown(msg.content)"
              />
              <span
                v-if="chatStore.isStreaming && index === chatStore.messages.length - 1"
                class="inline-block w-0.5 h-[1em] bg-amber-400 ml-0.5 align-middle animate-pulse rounded-full"
              />
              <div
                v-if="!isMessageBusy(msg, index) && getOfferActions(msg).length > 0"
                class="mt-1.5 px-3.5 py-2.5 rounded-xl bg-white"
                style="box-shadow: 0 1px 3px rgba(15,23,42,0.05); border: 1px solid rgba(0,74,173,0.14);"
              >
                <p class="text-[11px] font-medium text-gray-500 mb-2 flex items-center gap-1.5">
                  <svg class="w-3 h-3 shrink-0" style="color: #7f9cc7" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  {{ getNavigationHeading(msg.content) }}
                </p>
                <div class="flex flex-col gap-1.5">
                  <a
                    v-for="(action, actionIdx) in getOfferActions(msg)"
                    :key="`${index}-${action.route}-${actionIdx}`"
                    :href="navActions.getHref(action) ?? undefined"
                    class="cleo-nav-chip flex items-center gap-2 self-start text-xs font-medium px-3 py-1.5 rounded-full transition-all active:scale-95 no-underline"
                    @click.prevent="navActions.navigate(action)"
                  >
                    <svg class="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    {{ action.label }}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </template>

        <div
          v-if="chatStore.isSending && chatStore.messages[chatStore.messages.length - 1]?.role !== 'agent'"
          class="flex justify-start gap-2 items-end"
        >
          <div class="shrink-0 w-8 h-8 flex items-center justify-center">
            <CleoThinkingIcon size-class="w-8 h-8" />
          </div>
          <CleoWorkingIndicator />
        </div>

        <div
          v-if="chatStore.error"
          class="text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 flex items-start gap-2"
        >
          <svg class="w-3.5 h-3.5 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          {{ chatStore.error }}
        </div>
      </div>

      <div class="shrink-0 bg-white px-4 py-3" style="border-top: 1px solid rgba(15,23,42,0.06);">
        <div v-if="chatStore.quota && chatStore.quota.limit !== null" class="mb-2 px-1">
          <div class="flex items-center justify-between mb-1">
            <span class="text-xs font-semibold" :class="quotaExhausted ? 'text-[#b06060]' : 'text-[#46648f]'">
              {{ quotaExhausted ? 'Cleo limit reached' : 'Cleo responses' }}
            </span>
            <span class="text-xs font-semibold" :class="quotaExhausted ? 'text-[#b06060]' : 'text-[#46648f]'">
              {{ quotaText }}
            </span>
          </div>
          <div class="h-1.5 rounded-full overflow-hidden" style="background: rgba(0, 74, 173, 0.1)">
            <div
              class="h-full rounded-full transition-all duration-300"
              :style="{ width: quotaPercent + '%', background: quotaExhausted ? '#b06060' : '#7f9cc7' }"
            />
          </div>
        </div>
        <form class="cleo-input-shell flex items-end gap-1.5 rounded-2xl pl-3.5 pr-1.5 py-1.5" @submit.prevent="handleSend">
          <textarea
            ref="inputRef"
            v-model="inputText"
            rows="1"
            placeholder="Type a message…"
            class="flex-1 resize-none overflow-y-auto text-sm text-gray-800 bg-transparent py-2 focus:outline-none placeholder-gray-400"
            style="max-height: 120px; min-height: 40px;"
            @keydown.enter.exact.prevent="handleSend"
            @keydown.up="onHistoryUp"
            @keydown.down="onHistoryDown"
            @input="autoResize"
          />
          <button
            type="submit"
            class="cleo-send-btn shrink-0 flex items-center justify-center w-9 h-9 mb-0.5 rounded-full text-white transition-all active:scale-95 disabled:opacity-35 disabled:cursor-not-allowed disabled:active:scale-100"
            :disabled="!inputText.trim() || chatStore.isSending"
            aria-label="Send message"
          >
            <svg class="w-4 h-4 -ml-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
            </svg>
          </button>
        </form>
      </div>

      <div v-if="historyOpen" class="absolute inset-0 z-[54]" @click="historyOpen = false" />
      <Transition name="cleo-history-pop">
        <div
          v-if="historyOpen"
          class="absolute top-2 right-2 z-[55] flex flex-col rounded-xl overflow-hidden"
          style="width: min(400px, calc(100% - 16px)); max-height: min(440px, calc(100% - 16px)); background: white; box-shadow: 0 16px 48px rgba(0,30,80,0.20); border: 1px solid rgba(0,74,173,0.12);"
        >
          <div class="shrink-0 flex items-center justify-between px-3.5 pt-3 pb-1.5">
            <p class="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
              <svg class="w-3.5 h-3.5 shrink-0" style="color: #7f9cc7" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Recent conversations
            </p>
            <span v-if="!historyLoading" class="text-[10px] font-medium px-1.5 py-0.5 rounded-full" style="background: rgba(0,74,173,0.07); color: #004aad;">{{ filteredHistoryConversations.length }}</span>
          </div>

          <div class="shrink-0 px-2 pb-2" style="border-bottom: 1px solid rgba(15,23,42,0.06);">
            <div class="relative">
              <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-4.5-4.5M17 10.5a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" />
              </svg>
              <input
                v-model="historySearch"
                type="text"
                placeholder="Search conversations…"
                class="cleo-history-search w-full text-sm text-gray-700 rounded-lg pl-9 pr-3 py-2 focus:outline-none placeholder-gray-400"
              />
            </div>
          </div>

          <div v-if="historyLoading" class="py-10 flex items-center justify-center">
            <svg class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24" style="color: #004aad;">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          </div>

          <div v-else-if="historyConversations.length === 0" class="py-8 px-6 flex flex-col items-center gap-2">
            <svg class="w-8 h-8" style="color: #c3d2e8" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p class="text-sm text-center" style="color: rgba(0,44,120,0.35);">No past conversations.</p>
          </div>

          <div v-else class="cleo-scroll flex-1 overflow-y-auto p-1.5">
            <div v-if="filteredHistoryConversations.length === 0" class="px-4 py-6 flex flex-col items-center gap-2">
              <svg class="w-8 h-8" style="color: #c3d2e8" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-4.5-4.5M17 10.5a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" />
              </svg>
              <p class="text-sm text-center" style="color: rgba(0,44,120,0.35);">No matching conversations.</p>
            </div>

            <template v-for="group in groupedHistoryConversations" :key="group.label">
              <p class="px-2.5 pt-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wider" style="color: #9aa9c0;">{{ group.label }}</p>
              <button
                v-for="conv in group.items"
                :key="conv.id ?? 0"
                class="w-full flex items-center gap-2.5 text-left px-2.5 py-2 rounded-lg transition-colors"
                :class="chatStore.dbConversationId === conv.id ? 'bg-[#e9f1fc]' : 'hover:bg-[#f3f6fc]'"
                @click="loadHistoryConversation(conv)"
              >
                <span
                  class="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center"
                  :style="chatStore.dbConversationId === conv.id ? 'background: rgba(0,74,173,0.14)' : 'background: rgba(0,74,173,0.06)'"
                >
                  <svg class="w-3.5 h-3.5" style="color: #004aad" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </span>
                <span
                  class="flex-1 min-w-0 truncate text-sm"
                  :class="chatStore.dbConversationId === conv.id ? 'font-semibold text-[#004aad]' : 'text-gray-700'"
                >{{ conv.title ?? conv.reference ?? `Conversation #${conv.id}` }}</span>
                <span class="shrink-0 text-[11px] text-gray-400 tabular-nums">{{ relativeTime(conv) }}</span>
              </button>
            </template>
          </div>

          <div v-if="!historyLoading && historyConversations.length > 0" class="shrink-0 px-3 py-1.5 text-center" style="border-top: 1px solid rgba(15,23,42,0.06);">
            <p class="text-[10px] text-gray-400">Showing the last {{ HISTORY_DAYS }} days</p>
          </div>
        </div>
      </Transition>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useCleoStore } from '@/stores/cleoChat';
import { useApiStore } from '@/stores/api';
import { useAuthStore } from '@/stores/auth';
import type { TraceStep } from '@/types/ai/cleoChat';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import Swal from 'sweetalert2';
import { showError } from '@/services/ShowMessages';
import CleoThinkingIcon from '@/components/svg_icons/CleoThinkingIcon.vue';
import CleoMoodIcon from '@/components/svg_icons/CleoMoodIcon.vue';
import CleoToolPanel from '@/views/ai/components/Cleo/CleoToolPanel.vue';
import CleoWorkingIndicator from '@/views/ai/components/Cleo/CleoWorkingIndicator.vue';
import CleoErrorBubble from '@/views/ai/components/Cleo/CleoErrorBubble.vue';
import { CleoConversation } from '@/types/ai/cleoConversation';
import type { CleoConversationData } from '@/types/ai/cleoConversation';
import { InterfaceFilterMultipleSeparator } from '@/types/object_classes/InterfaceListingFilter';
import { useNavigationActions, mapOffersToActions, getNavigationHeading } from '@/helpers/useNavigationActions';
import type { ChatMessage } from '@/types/ai/cleoChat';

const chatStore = useCleoStore();
const api = useApiStore();
const authStore = useAuthStore();
const navActions = useNavigationActions();

const quotaExhausted = computed(() => {
  return chatStore.quota !== null && chatStore.quota.limit !== null && (chatStore.quota.remaining ?? 0) <= 0;
});

const quotaText = computed(() => {
  const quota = chatStore.quota;
  if (!quota || quota.limit === null) return '';
  const periodWord = quota.period === 'week' ? 'this week' : quota.period === 'month' ? 'this month' : 'today';
  return `${quota.remaining ?? 0} of ${quota.limit} left ${periodWord}`;
});

const quotaPercent = computed(() => {
  const quota = chatStore.quota;
  if (!quota || quota.limit === null || quota.limit === 0) return 0;
  return Math.min(100, Math.max(0, ((quota.remaining ?? 0) / quota.limit) * 100));
});
const inputText = computed({
  get: () => chatStore.draft,
  set: (value: string) => {
    chatStore.draft = value;
  },
});
const messagesContainer = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLTextAreaElement | null>(null);

// ── Resizable width (state lives in the store so the layout reserves matching space) ──
let resizeStartX = 0;
let resizeStartWidth = 0;

const onResize = (e: PointerEvent) => {
  const delta = resizeStartX - e.clientX;
  chatStore.sidePanelWidth = chatStore.clampSidePanelWidth(resizeStartWidth + delta);
}

const stopResize = () => {
  window.removeEventListener('pointermove', onResize);
  window.removeEventListener('pointerup', stopResize);
  document.body.style.userSelect = '';
  chatStore.isResizingSidePanel = false;
  chatStore.persistSidePanelWidth();
}

const startResize = (e: PointerEvent) => {
  e.preventDefault();
  resizeStartX = e.clientX;
  resizeStartWidth = chatStore.sidePanelWidth;
  chatStore.isResizingSidePanel = true;
  document.body.style.userSelect = 'none';
  window.addEventListener('pointermove', onResize);
  window.addEventListener('pointerup', stopResize);
}

// ── History panel ────────────────────────────────────────────────────────────
const HISTORY_DAYS = 30;
const HISTORY_MAX = 200;
const historyOpen = ref(false);
const historyLoading = ref(false);
const historyConversations = ref<CleoConversation[]>([]);
const historySearch = ref('');

const filteredHistoryConversations = computed(() => {
  const q = historySearch.value.trim().toLowerCase();
  if (!q) return historyConversations.value;
  return historyConversations.value.filter(conv =>
    (conv.title ?? conv.reference ?? '').toLowerCase().includes(q),
  );
});

const groupedHistoryConversations = computed(() => {
  const groups: Array<{ label: string; items: CleoConversation[] }> = [];
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  const bucketFor = (conv: CleoConversation): string => {
    if (!conv.created_at) return 'Earlier';
    const d = conv.created_at instanceof Date ? conv.created_at : new Date(conv.created_at);
    if (d.getTime() >= startOfToday) return 'Today';
    if (d.getTime() >= startOfToday - dayMs) return 'Yesterday';
    if (d.getTime() >= startOfToday - 7 * dayMs) return 'Last 7 days';
    return 'Earlier';
  };
  for (const conv of filteredHistoryConversations.value) {
    const label = bucketFor(conv);
    const group = groups.find(g => g.label === label);
    if (group) {
      group.items.push(conv);
    } else {
      groups.push({ label, items: [conv] });
    }
  }
  return groups;
});

const relativeTime = (conv: CleoConversation): string => {
  if (!conv.created_at) return '';
  const d = conv.created_at instanceof Date ? conv.created_at : new Date(conv.created_at);
  const minutes = Math.floor((Date.now() - d.getTime()) / 60000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  return `${Math.floor(months / 12)}y`;
}

const historyDateRange = (): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - HISTORY_DAYS);
  from.setHours(0, 0, 0, 0);
  return `${fmt(from)}, ${fmt(to)}`;
}

const openHistory = async () => {
  if (historyOpen.value) {
    historyOpen.value = false;
    return;
  }
  historyOpen.value = true;
  historyLoading.value = true;
  historyConversations.value = [];
  historySearch.value = '';
  try {
    const userId = authStore.user?.id;
    if (!userId) return;
    const filters = [
      { field: 'user_id', delimiter: InterfaceFilterMultipleSeparator.SEPARATOR, values: [String(userId)] },
      { field: 'created_at', delimiter: InterfaceFilterMultipleSeparator.SEPARATOR, values: [historyDateRange()] },
    ];
    historyConversations.value = await api.getItems<CleoConversation, CleoConversationData>(
      'cleo-conversations',
      CleoConversation,
      undefined,
      undefined,
      filters,
      { page_number: 1, page_size: HISTORY_MAX },
      ['-id'],
    );
  } finally {
    historyLoading.value = false;
  }
}

const loadHistoryConversation = async (conv: CleoConversation) => {
  historyOpen.value = false;
  await chatStore.loadConversation(conv);
}

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key !== 'Escape') return;
  if (historyOpen.value) {
    historyOpen.value = false;
    return;
  }
  if (chatStore.isSidePanelOpen) chatStore.closeSidePanel();
}

const traceStepsToEvents = (steps: TraceStep[]) =>
  steps.map(s => ({ event: s.event, plugin: s.plugin, fn: s.function, args: s.args ?? null, result: s.result ?? null, error: s.error ?? null }));

const renderMarkdown = (content: string | undefined): string => {
  if (!content) return '';
  const html = marked.parse(content, { async: false }) as string;
  return DOMPurify.sanitize(html);
}

const groupTraceSteps = (steps: TraceStep[]) => {
  const calls: Array<{
    plugin: string;
    fn: string;
    args?: Record<string, string>;
    result?: string;
    error?: string;
    status: 'pending' | 'done' | 'error';
  }> = [];
  for (const step of steps) {
    if (step.event === 'tool_call') {
      calls.push({ plugin: step.plugin, fn: step.function, args: step.args, status: 'pending' });
    } else if (step.event === 'tool_result') {
      const match = [...calls].reverse().find(c => c.plugin === step.plugin && c.fn === step.function && c.status === 'pending');
      if (match) { match.result = step.result; match.status = 'done'; }
    } else if (step.event === 'tool_error') {
      const match = [...calls].reverse().find(c => c.plugin === step.plugin && c.fn === step.function && c.status === 'pending');
      if (match) { match.error = step.error; match.status = 'error'; }
    }
  }
  return calls;
}

const getPendingFn = (steps: TraceStep[] | undefined): string | undefined => {
  if (!steps || steps.length === 0) return undefined;
  return [...groupTraceSteps(steps)].reverse().find(c => c.status === 'pending')?.fn;
}

const lastAgentMsg = computed(() =>
  [...chatStore.messages].reverse().find(m => m.role === 'agent'),
);

const getOfferActions = (msg: ChatMessage) => {
  return mapOffersToActions(msg.navigationOffers).filter(navActions.hasAccess);
}

// Hide the chooser while the assistant is still streaming the latest message —
// offers arrive at the very end of a turn, so showing them mid-stream would flicker.
const isMessageBusy = (msg: ChatMessage, index: number): boolean => {
  if (index !== chatStore.messages.length - 1) return false;
  if (chatStore.isSending || chatStore.isStreaming) return true;
  return !!msg.error;
}

const handleSend = async () => {
  const text = inputText.value.trim();
  if (!text || chatStore.isSending) return;
  inputText.value = '';
  resetTextareaHeight();
  await chatStore.send(text);
}

const handleReset = async () => {
  await chatStore.reset();
}

const handleReportProblem = async () => {
  if (chatStore.dbConversationId === null || chatStore.isReported) return;
  const result = await Swal.fire({
    title: 'Report a problem',
    text: 'Describe the problems you encountered in this conversation (optional). Your report will be sent to our team together with the conversation.',
    input: 'textarea',
    inputPlaceholder: 'What went wrong?',
    inputAttributes: { maxlength: '255', rows: '6' },
    showCancelButton: true,
    confirmButtonText: 'Send report',
    cancelButtonText: 'Cancel',
  });
  if (!result.isConfirmed) return;
  const reason = typeof result.value === 'string' && result.value.trim() !== '' ? result.value.trim() : null;
  const reported = await chatStore.reportProblem(reason);
  if (reported) {
    const referencePart = chatStore.conversationReference
      ? '<p>Conversation reference: <b>' + chatStore.conversationReference + '</b></p>'
      : '';
    Swal.fire({
      title: 'Report sent',
      html: '<p>Thank you! Your report has been sent and our team will review this conversation.</p>' + referencePart,
      icon: 'success',
      showCancelButton: false,
    });
  } else {
    showError('Report failed', 'An error has occured when sending your report. Please try again.');
  }
}

const autoResize = (event: Event) => {
  const el = event.target as HTMLTextAreaElement;
  el.style.height = 'auto';
  el.style.height = Math.min(el.scrollHeight, 120) + 'px';
}

const syncTextareaHeight = () => {
  if (!inputRef.value) return;
  inputRef.value.style.height = 'auto';
  inputRef.value.style.height = Math.min(inputRef.value.scrollHeight, 120) + 'px';
}

const onHistoryUp = (event: KeyboardEvent) => {
  const el = event.target as HTMLTextAreaElement;
  if (el.value.slice(0, el.selectionStart ?? 0).includes('\n')) return;
  if (!chatStore.historyPrev()) return;
  event.preventDefault();
  nextTick(() => {
    syncTextareaHeight();
    el.setSelectionRange(el.value.length, el.value.length);
  });
}

const onHistoryDown = (event: KeyboardEvent) => {
  const el = event.target as HTMLTextAreaElement;
  if (el.value.slice(el.selectionEnd ?? el.value.length).includes('\n')) return;
  if (!chatStore.historyNext()) return;
  event.preventDefault();
  nextTick(() => {
    syncTextareaHeight();
    el.setSelectionRange(el.value.length, el.value.length);
  });
}

const resetTextareaHeight = () => {
  if (inputRef.value) inputRef.value.style.height = '40px';
}

const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
}

watch(() => chatStore.messages.length, scrollToBottom);
watch(
  () => chatStore.messages[chatStore.messages.length - 1]?.content,
  () => { if (chatStore.isStreaming) scrollToBottom(); },
);
watch(
  () => chatStore.messages[chatStore.messages.length - 1]?.traceSteps?.length,
  () => { if (chatStore.isSending) scrollToBottom(); },
);
watch(
  () => chatStore.isSending,
  (sending) => {
    if (!sending) nextTick(() => inputRef.value?.focus());
  },
);
watch(
  () => chatStore.isSidePanelOpen,
  (open) => {
    if (open) {
      chatStore.fetchQuota();
      if (chatStore.messages.length === 0 && !chatStore.isGreeting) chatStore.greet();
      nextTick(() => {
        inputRef.value?.focus();
        syncTextareaHeight();
      });
    } else {
      historyOpen.value = false;
    }
  },
);

onMounted(() => window.addEventListener('keydown', handleKeydown));
onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
  window.removeEventListener('pointermove', onResize);
  window.removeEventListener('pointerup', stopResize);
});
</script>

<style scoped>
.cleo-panel-slide-enter-active,
.cleo-panel-slide-leave-active {
  transition: transform 0.28s cubic-bezier(0.34, 1.1, 0.64, 1), opacity 0.22s ease;
}
.cleo-panel-slide-enter-from,
.cleo-panel-slide-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.cleo-history-pop-enter-active,
.cleo-history-pop-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
  transform-origin: top right;
}
.cleo-history-pop-enter-from,
.cleo-history-pop-leave-to {
  opacity: 0;
  transform: scale(0.96) translateY(-4px);
}

.cleo-resize-handle:hover {
  background: rgba(0, 74, 173, 0.18);
}

.cleo-header-btn {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
}
.cleo-header-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}
.cleo-header-btn.cleo-header-btn-active {
  background: rgba(255, 255, 255, 0.28);
}

.cleo-scroll::-webkit-scrollbar {
  width: 6px;
}
.cleo-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.cleo-scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 74, 173, 0.16);
  border-radius: 999px;
}
.cleo-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 74, 173, 0.3);
}

.cleo-topic-pill {
  font-size: 10px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(0, 74, 173, 0.07);
  color: #004aad;
  border: 1px solid rgba(0, 74, 173, 0.14);
}

.cleo-nav-chip {
  background: rgba(0, 74, 173, 0.06);
  color: #004aad;
  border: 1px solid rgba(0, 74, 173, 0.16);
}
.cleo-nav-chip:hover {
  background: rgba(0, 74, 173, 0.12);
  border-color: rgba(0, 74, 173, 0.32);
  transform: translateX(2px);
}

.cleo-input-shell {
  background: #f6f8fc;
  border: 1px solid rgba(15, 23, 42, 0.1);
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}
.cleo-input-shell:focus-within {
  background: #fff;
  border-color: rgba(0, 74, 173, 0.45);
  box-shadow: 0 0 0 3px rgba(0, 74, 173, 0.1);
}

.cleo-send-btn {
  background: linear-gradient(135deg, #0a58c4, #003a8a);
  box-shadow: 0 2px 8px rgba(0, 74, 173, 0.35);
}
.cleo-send-btn:hover:not(:disabled) {
  box-shadow: 0 4px 12px rgba(0, 74, 173, 0.45);
  transform: translateY(-1px);
}

.cleo-history-search {
  background: #f6f8fc;
  border: 1px solid rgba(15, 23, 42, 0.1);
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}
.cleo-history-search:focus {
  background: #fff;
  border-color: rgba(0, 74, 173, 0.45);
  box-shadow: 0 0 0 3px rgba(0, 74, 173, 0.1);
}

.cleo-markdown :deep(p) { margin: 0 0 0.5em; }
.cleo-markdown :deep(p:last-child) { margin-bottom: 0; }
.cleo-markdown :deep(h1),
.cleo-markdown :deep(h2),
.cleo-markdown :deep(h3),
.cleo-markdown :deep(h4) { font-weight: 700; margin: 0.65em 0 0.3em; line-height: 1.3; }
.cleo-markdown :deep(h1) { font-size: 1.1em; }
.cleo-markdown :deep(h2) { font-size: 1em; }
.cleo-markdown :deep(h3) { font-size: 0.95em; }
.cleo-markdown :deep(ul),
.cleo-markdown :deep(ol) { padding-left: 1.3em; margin: 0.3em 0 0.5em; }
.cleo-markdown :deep(ul) { list-style-type: disc; }
.cleo-markdown :deep(ol) { list-style-type: decimal; }
.cleo-markdown :deep(li) { margin: 0.15em 0; }
.cleo-markdown :deep(code) {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.83em;
  background: rgba(0, 74, 173, 0.07);
  color: #1e3a6e;
  padding: 0.1em 0.3em;
  border-radius: 4px;
}
.cleo-markdown :deep(pre) {
  background: #f1f5f9;
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 7px;
  padding: 0.6em 0.85em;
  overflow-x: auto;
  margin: 0.4em 0;
}
.cleo-markdown :deep(pre code) { background: transparent; padding: 0; color: #1e293b; }
.cleo-markdown :deep(blockquote) { border-left: 3px solid #004aad; padding-left: 0.6em; margin: 0.4em 0; color: #4b5563; }
.cleo-markdown :deep(strong) { font-weight: 700; }
.cleo-markdown :deep(em) { font-style: italic; }
.cleo-markdown :deep(a) { color: #004aad; text-decoration: underline; }
.cleo-markdown :deep(hr) { border: none; border-top: 1px solid rgba(0, 0, 0, 0.1); margin: 0.6em 0; }
.cleo-markdown :deep(table) { width: 100%; border-collapse: collapse; font-size: 0.82em; margin: 0.4em 0; }
.cleo-markdown :deep(th),
.cleo-markdown :deep(td) { border: 1px solid rgba(0, 74, 173, 0.15); padding: 0.25em 0.5em; text-align: left; }
.cleo-markdown :deep(th) { background: rgba(0, 74, 173, 0.06); font-weight: 600; }
</style>
